import { useCallback, useRef } from 'react';
import type { SiteId, TaskId, WritableLocationStatus } from '@interfaces/IScheduler';
import { useImmediateWritePersist } from './useImmediateWritePersist';
import { useSchedulerActions } from './useSchedulerStore';
import {
  AREA_REPORTS_CHANNEL,
  areaReport,
  mergeAreaReports,
  sendAreaReport,
  type IAreaReportWrite,
} from '../lib/areaReportWrite';

/**
 * Immediate-write persistence for a task's per-area STATUS — one of the three
 * status facts D-ORCA writes to Trusstor (2026-09-17 task-commit design,
 * invariant 5): `complete` (done), `readyForReview` (submitted for review) and
 * `planned`, which clears either (area review stage spec, ruling 2).
 * Everything else about an area's status is Trusstor's own computation from
 * tag work time.
 *
 * Area ASSIGNMENT is NOT here: it is a plan fact, saved through the plan
 * auto-save (`useTaskPlanAutoSave`), not the execution channels.
 *
 * `report(locationIds, status, { holdProgress })` rides ONE channel per task
 * (`immediateWriteChannel`, shared with the Progress Tracker —
 * `areaReportWrite`): taps, a sweep and a floor press merge into one request,
 * and requests stay serialized. The server derives the task % those
 * areas move and stores it AFTER Trusstor took them (QA R-04) — the drawer no
 * longer PUTs a % beside the areas, so a failed area write can't leave a %
 * behind. The store's optimistic % is then replaced by the server's, unless a
 * newer report is already on screen (its own response settles it) or the
 * session holds the %. A report given up is rolled back to the server's and a
 * toast says so (`useImmediateWritePersist`). No-op when `scheduleNumber` is
 * absent (Router-free tests) — the store still updates.
 */
export function useTaskLocationsPersist(
  siteId: SiteId,
  scheduleNumber: string | undefined,
  taskId: TaskId,
): { report: (locationIds: readonly string[], status: WritableLocationStatus, options: { holdProgress: boolean }) => void } {
  const { write } = useImmediateWritePersist(siteId, scheduleNumber, taskId);
  const { taskProgressSet } = useSchedulerActions();
  // How many reports this surface has made — a response settles the store's %
  // only when no report came after the ones it carried.
  const reportsRef = useRef(0);

  const report = useCallback(
    (locationIds: readonly string[], status: WritableLocationStatus, { holdProgress }: { holdProgress: boolean }) => {
      reportsRef.current += 1;
      write<IAreaReportWrite>(
        [AREA_REPORTS_CHANNEL],
        areaReport(locationIds, status, holdProgress),
        async (value, ctx) => {
          const reportsSent = reportsRef.current;
          const { progressPercent } = await sendAreaReport(ctx, value);
          if (!value.holdProgress && reportsRef.current === reportsSent) {
            taskProgressSet({ siteId: ctx.siteId, taskId: ctx.taskId, progressPercent });
          }
        },
        { coalesce: mergeAreaReports },
      );
    },
    [write, taskProgressSet],
  );

  return { report };
}
